import { useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import type { AIScanVideoActiveWorkTriggerState, AIScanVideoUploadModalVariant } from '../AIScanVideoUploadModal';
import { FocusedScopeEditor, type FocusedScopeEditorProps } from '../FocusedScopeEditor';
import { FocusPanelNewVideoButton, FocusPanelTitlebar } from './FocusPanelTitlebar';
import type { ScanFocusPanelScopeProps } from './scanFocusPanelTypes';

export type FocusedEditPanelProps = ScanFocusPanelScopeProps & {
  focusedScopeEditor: Omit<FocusedScopeEditorProps, 'onOpenVideoUpload' | 'walkthroughUploadEnabled' | 'aiScanVideoActiveCount'>;
  /** Opens the scan-page upload modal — `'origin'` from empty state, `'add'` from the titlebar. */
  onOpenVideoUpload?: (variant: AIScanVideoUploadModalVariant) => void;
  aiScanVideoActiveWork?: AIScanVideoActiveWorkTriggerState;
};

export const FocusedEditPanel = ({
  focusedScopeEditor,
  onOpenVideoUpload,
  aiScanVideoActiveWork,
  selectedTaskId,
  ...scope
}: FocusedEditPanelProps) => {
  const { featureFlags } = useFeatureFlags();
  const { latestScan, latestApprovedScan, scanHasScopedTasks, projectIsEditable } = useScanEditingContextSlice([
    'latestScan',
    'latestApprovedScan',
    'scanHasScopedTasks',
    'projectIsEditable',
  ]);

  const walkthroughUploadEnabled = !!latestScan
    && !!projectIsEditable
    && !latestApprovedScan
    && latestScan.scanLifecycleStatus === 'dispatched'
    && featureFlags.useAiVideoScan;

  const showNewVideoButton = !!onOpenVideoUpload && scanHasScopedTasks && walkthroughUploadEnabled;

  const activeCount = aiScanVideoActiveWork?.activeCount ?? 0;

  const trailing = showNewVideoButton
    ? <FocusPanelNewVideoButton activeCount={activeCount} onClick={() => onOpenVideoUpload('add')} />
    : undefined;

  return (
    <FocusPanelTitlebar {...scope} activePanel='edit' bodyVariant='flush' trailing={trailing}>
      <FocusedScopeEditor
        {...focusedScopeEditor}
        walkthroughUploadEnabled={walkthroughUploadEnabled}
        aiScanVideoActiveCount={activeCount}
        onOpenVideoUpload={onOpenVideoUpload ? () => onOpenVideoUpload('origin') : undefined}
        taskId={selectedTaskId ?? focusedScopeEditor.taskId}
      />
    </FocusPanelTitlebar>
  );
};
